import type { NextPageContext } from 'next';
import type { IncomingHttpHeaders } from 'http';
import {
  RUTTER_PREFIX, SEARCH_PREFIX, LISTING_API_PREFIX, PRICES_API_PREFIX,
} from '../constants';
import ApiResponseError from './apiResponseError';
import isAbortedError from './isAbortedError';
import appendLangParam from './appendLangParam';
import retry from './retry';

// Reducing the surface area of the type to the minimum required props to increase compatibility
// between the context types.
type RequestContext = Pick<NextPageContext, 'req' | 'locale'>;

const REQUEST_ID = 'x-request-id';

/**
 * We allow GET server-side calls to be attempted twice.
 * This is to cater for fetch related errors:
 * - `getaddrinfo EAI_AGAIN` DNS lookup timeouts
 * - `connect ECONNREFUSED` TCP connection issues
 */
const MAX_GET_API_CALL_ATTEMPTS = 2;
const MAX_NON_GET_API_CALL_ATTEMPTS = 1;

const headerAllowlist = [
  'x-real-ip',
  'x-forwarded-for',
  REQUEST_ID,
  'remote_addr',
  'cookie',
  'host',
];

// filter the incoming headers to the header allowlist.
const filterHeaders = (headers: IncomingHttpHeaders): HeadersInit => (
  headerAllowlist.reduce((newHeaders, key) => {
    // eslint-disable-next-line no-param-reassign
    if (headers[key]) newHeaders[key] = headers[key];
    return newHeaders;
  }, {} as HeadersInit));

const updateOptions = (
  context: RequestContext,
  options: RequestInit,
) => {
  const update = { ...options };
  const requestHeaders = context?.req?.headers ?? {};

  // Filter incoming headers and set some new ones to be passed on to the API service
  const headers: HeadersInit = {
    ...filterHeaders(requestHeaders),
    'content-type': 'application/json',
    'user-agent': (requestHeaders['user-agent'] === 'GetMyBot' ? 'GetMyBot' : 'GetMyBoat/Next'),
    'x-forwarded-for': `${requestHeaders['x-real-ip']}`,
    'x-forwarded-proto': 'https',
  };

  // Take the supplied headers and override them with the filtered and updated incoming headers
  update.headers = {
    ...headers,
    ...update.headers,
  };

  return update;
};

const propagateRequestIdHeaders = (context: RequestContext): HeadersInit => (
  [
    [REQUEST_ID, `${context?.req?.headers[REQUEST_ID]}`],
  ]
);

type Fetcher = <T = unknown>(
  context: RequestContext,
  uri: string,
  init?: RequestInit) => Promise<T>;

const API_TIMEOUT = process.env.API_TIMEOUT ? Number(process.env.API_TIMEOUT) || 3000 : 3000;

/**
 * Fetch wrapper for calling an API path.
 *
 * _NOTE_: This implementation must only be used for server initiated api calls from
 * within `getInitialProps(context: NextPageContext)`
 * or `getServerSideProps(context: NextPageContext)`.
 * It works the same as the `apiFetch()` util, but the `NextPageContext` needs to be
 * included as the first param.
 * @param context The context object with the incoming request `req`.
 * @param uri The API path.
 * @param init Optional init options.
 */
const serverApiFetch: Fetcher = async (context, uri, init = {}) => {
  let apiHost = process.env.API_HOSTNAME_PROTOCOL;
  // If this is a call to the search API then we set the apiHost
  if (uri.startsWith(SEARCH_PREFIX)) {
    apiHost = process.env.SEARCH_API_HOSTNAME_PROTOCOL;
  } else if (uri.startsWith(RUTTER_PREFIX)) {
    apiHost = process.env.RUTTER_API_HOSTNAME_PROTOCOL;
  } else if (uri.startsWith(LISTING_API_PREFIX)) {
    apiHost = process.env.LISTING_API_HOSTNAME_PROTOCOL;
  } else if (uri.startsWith(PRICES_API_PREFIX)) {
    apiHost = process.env.PRICES_API_HOSTNAME_PROTOCOL;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, API_TIMEOUT);

  const requester = () => fetch(appendLangParam(`${apiHost}${uri}`, context.locale), {
    ...updateOptions(context, init),
    signal: controller.signal,
  });

  try {
    /**
     * Fallback for when retry count is depleted.
     */
    const fallback = () => Promise.resolve(new Response(
      'Unable to reach backend',
      {
        headers: propagateRequestIdHeaders(context),
        status: 500,
        statusText: 'Unable to reach backend',
      },
    ));

    const method = init.method?.toUpperCase() ?? 'GET';
    const maxApiCallAttempts = method === 'GET' ? MAX_GET_API_CALL_ATTEMPTS : MAX_NON_GET_API_CALL_ATTEMPTS;

    const response = await retry(maxApiCallAttempts, requester, fallback);

    if (!response.ok) {
      throw new ApiResponseError(response);
    }

    const contentType = response.headers.get('Content-Type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Cannot parse non JSON response.');
    }

    return await response.json();
  } catch (err) {
    if (isAbortedError(err)) {
      throw new ApiResponseError(new Response(
        'API Request Timeout',
        {
          headers: propagateRequestIdHeaders(context),
          status: 500,
          statusText: 'API Request Timeout',
        },
      ));
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

export default serverApiFetch;
